import type { AttributionTraits } from "./types";

/**
 * List of all attribution parameters we want to capture from query strings.
 * This includes UTM parameters and click IDs from self-attributing networks (SANs).
 *
 * References: AttributionTraits
 */
const ATTRIBUTION_PARAMS: readonly Exclude<
  keyof AttributionTraits,
  "attribution_timestamp"
>[] = [
  // UTM parameters
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
  "utm_id",
  "utm_source_platform", // sometimes included by Google Ads
  "utm_creative_format", // creative type
  "utm_marketing_tactic", // extended tracking

  // Self-attributing network (SAN) click IDs
  "gclid", // Google Ads
  "wpkwn", // Google Ad words
  "wbraid", // Google Ad words Google iOS web-to-app conversions
  "gbraid", // Google iOS app conversions
  "fbclid", // Facebook
  "ttclid", // TikTok
  "twclid", // Twitter
  "li_fat_id", // LinkedIn
  "ScCid", // Snapchat
  "irclickid", // Impact
  "msclkid", // Microsoft Ads
  "yclid", // Yandex Ads
  "vcid", // Verizon / Yahoo Ads

  // Apple Search Ads
  "asa",
  "asa_campaign",
  "asa_adgroup",
  "asa_keyword",
  // Apple Search Ads V2
  "campaignId",
  "adgroupId",
  "keywordId",
  "creativeSetId",

  // Complete Meta
  "fbadid",
  "fbadname",
  "fbadsetid",
  "fbadsetname",
  "fbcampaignid",
  "fbcampaignname",
  "igshid",
  "ig_rid",
  // Complete Instagram (Old)
  "igshid",
  "ig_rid",
  "ig_mid",

  // Other common attribution parameters
  "referrer",
  "ref",
  "source",
  "ref_id", // affiliate/referral code
  "pid", // affiliate partner ID
  "cid", // campaign/customer ID
] as const;

/**
 * Extracts attribution parameters from URL query parameters.
 * Only includes parameters that are present in the query string.
 *
 * @param queryParams - Query parameters from URL (e.g., from Next.js router.query)
 * @returns Attribution traits object with only the parameters that were present
 */
export function extractAttributionTraits(
  queryParams: Record<string, string | string[] | undefined>,
): AttributionTraits {
  const traits: AttributionTraits = {};
  let hasAttribution = false;

  for (const param of ATTRIBUTION_PARAMS) {
    const value = queryParams[param];
    if (value && typeof value === "string") {
      // Type assertion is safe here because we're iterating over known keys
      (traits as Record<string, string>)[param] = value;
      hasAttribution = true;
    }
  }

  // Add timestamp if we found any attribution parameters
  if (hasAttribution) {
    traits.attribution_timestamp = new Date().toISOString();
  }

  return traits;
}

/**
 * Checks if the query parameters contain any attribution data.
 *
 * @param queryParams - Query parameters from URL
 * @returns true if any attribution parameters are present
 */
export function hasAttributionParams(
  queryParams: Record<string, string | string[] | undefined>,
): boolean {
  return ATTRIBUTION_PARAMS.some(param => {
    const value = queryParams[param];
    return value && typeof value === "string";
  });
}
